import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Loader2, ShieldCheck, Upload, FileText, Trash2, Plus, X, Award, Briefcase,
} from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import {
  popUpdateProfile, ROLES, ENGAGEMENT_ROUTES,
  popUploadResume, popDeleteResume, popResumeDownloadUrl,
} from '../../lib/popApi';

const TagInput = ({ label, values, setValues, placeholder, testIdPrefix }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v || values.includes(v)) return;
    setValues([...values, v]);
    setDraft('');
  };
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          data-testid={`${testIdPrefix}-input`}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
        <button onClick={add} type="button" data-testid={`${testIdPrefix}-add`}
          className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-xs font-semibold">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map((v, i) => (
            <span key={`${v}-${i}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200">
              {v}
              <button onClick={() => setValues(values.filter((x) => x !== v))} type="button"
                data-testid={`${testIdPrefix}-remove-${i}`}
                className="hover:text-white"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ResumeBox = ({ user, refresh }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setErr('Resume must be ≤ 10 MB'); return; }
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!['.pdf', '.doc', '.docx'].includes(ext)) {
      setErr('Only PDF, DOC and DOCX are allowed'); return;
    }
    setBusy(true); setErr(''); setMsg(''); setPct(0);
    try {
      await popUploadResume(file, (e) => {
        if (e.total) setPct(Math.round((e.loaded * 100) / e.total));
      });
      setMsg('Uploaded ✓');
      await refresh();
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'Upload failed');
    } finally { setBusy(false); setPct(0); }
  };

  const doDelete = async () => {
    setBusy(true); setErr('');
    try { await popDeleteResume(); await refresh(); setMsg('Removed'); setTimeout(() => setMsg(''), 1500); }
    catch (_e) { setErr('Could not remove'); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (busy) return;
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) doUpload(file);
  };

  const hasResume = !!(user && user.resume_filename);
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">Resume</label>
      {hasResume ? (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="resume-status-uploaded">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-emerald-500/15 flex items-center justify-center">
              <FileText className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white/90 font-medium truncate">{user.resume_filename}</div>
              <div className="text-[10px] text-white/50">
                {(() => {
                  const b = user.resume_size_bytes || 0;
                  if (b < 1024) return `${b} B`;
                  if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
                  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
                })()} · uploaded {user.resume_uploaded_at ? new Date(user.resume_uploaded_at).toLocaleDateString() : 'recently'}
              </div>
            </div>
            <a href={popResumeDownloadUrl(user.user_id)} target="_blank" rel="noopener noreferrer"
              data-testid="resume-view"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-xs font-semibold">
              View
            </a>
            <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="resume-replace"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-xs font-semibold">
              Replace
            </button>
            <button onClick={doDelete} disabled={busy} data-testid="resume-delete"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-rose-500/10 border border-rose-400/30 text-rose-300 text-xs font-semibold">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="resume-dropzone"
          className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition ${
            busy ? 'border-amber-300/50 bg-amber-300/5' : 'border-white/15 hover:border-amber-300/40 hover:bg-white/[0.02]'
          }`}
        >
          <Upload className="w-6 h-6 text-white/40 mx-auto mb-2" />
          <div className="text-sm text-white/70">
            {busy ? `Uploading… ${pct}%` : 'Drag & drop your resume, or click to browse'}
          </div>
          <div className="text-[10px] text-white/40 mt-1">PDF · DOC · DOCX · max 10 MB</div>
        </div>
      )}
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" data-testid="resume-file-input"
        className="hidden" onChange={(e) => e.target.files && doUpload(e.target.files[0])} />
      {err && <div className="text-xs text-rose-300 mt-2">{err}</div>}
      {msg && <div className="text-xs text-emerald-300 mt-2">{msg}</div>}
    </div>
  );
};

const PoPOnboarding = () => {
  const { user, refresh, loading } = usePoPAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [institution, setInstitution] = useState('');
  const [industryOrg, setIndustryOrg] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [location, setLocation] = useState('');
  const [routes, setRoutes] = useState([]);
  const [years, setYears] = useState('');
  const [projects, setProjects] = useState([]);
  const [patents, setPatents] = useState([]);
  const [certs, setCerts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [domains, setDomains] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const isEditing = user && user.role !== 'unassigned' && user.role !== 'super_admin';

  // Prefill from existing profile when editing
  useEffect(() => {
    if (!loading && !user) { navigate('/pop', { replace: true }); return; }
    if (user) {
      setRole(user.role !== 'unassigned' ? user.role : '');
      setHeadline(user.headline || '');
      setBio(user.bio || '');
      setInstitution(user.institution || '');
      setIndustryOrg(user.industry_org_name || '');
      setLinkedin(user.linkedin_url || '');
      setLocation(user.location || '');
      setRoutes(user.engagement_routes || []);
      setYears(user.years_experience ? String(user.years_experience) : '');
      setProjects(user.key_projects || []);
      setPatents(user.patents_publications || []);
      setCerts(user.certifications || []);
      setSectors(user.sectors || []);
      setDomains(user.domains || []);
    }
  }, [user, loading, navigate]);

  const toggleRoute = (r) => {
    setRoutes((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  };

  const submit = async () => {
    if (!role) { setErr('Please choose your role to continue'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const payload = {
        role,
        headline: headline || undefined,
        bio: bio || undefined,
        institution: institution || undefined,
        industry_org_name: industryOrg || undefined,
        linkedin_url: linkedin || undefined,
        location: location || undefined,
        engagement_routes: role === 'industry_expert' ? routes : undefined,
      };
      if (role === 'industry_expert' || role === 'faculty') {
        payload.years_experience = years ? parseInt(years, 10) : undefined;
        payload.key_projects = projects;
        payload.patents_publications = patents;
        payload.certifications = certs;
        payload.sectors = sectors;
        payload.domains = domains;
      }
      await popUpdateProfile(payload);
      const me = await refresh();
      if (!isEditing) {
        navigate(me?.role === 'super_admin' ? '/pop/admin' : '/pop/dashboard', { replace: true });
      } else {
        setMsg('Profile updated · verification queue notified');
        setTimeout(() => setMsg(''), 2500);
      }
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not save profile');
    } finally { setBusy(false); }
  };

  if (loading || !user) {
    return (
      <PoPShell>
        <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading…</div>
      </PoPShell>
    );
  }

  const showExpertFields = role === 'industry_expert' || role === 'faculty';
  const showInstitutionField = ['university', 'faculty', 'student'].includes(role);
  const showOrgField = ['industry_expert', 'industry_org'].includes(role);
  const showResumeBox = ['industry_expert', 'faculty'].includes(role);

  return (
    <PoPShell activeTab="profile">
      <div className="max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/30 bg-amber-300/5 mb-4">
          <GraduationCap className="w-3.5 h-3.5 text-amber-300" />
          <span className="text-[10px] tracking-[0.3em] font-bold text-amber-300">
            {isEditing ? 'EDIT YOUR PROFILE' : 'STEP 1 · CHOOSE YOUR ROLE'}
          </span>
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-2">
          {isEditing ? `Update your profile, ${user.name.split(' ')[0]}.` : `Welcome, ${user.name.split(' ')[0]}.`}
        </h1>
        <p className="text-sm text-white/60 mb-8">
          {isEditing
            ? 'Universities and HEIs use this profile to discover, evaluate, and invite you as a Professor of Practice.'
            : 'Tell us who you are so we can tailor your dashboard and connections. You can update these later.'}
        </p>

        {/* Role selection (disabled once set) */}
        <div className="space-y-2 mb-8">
          {ROLES.map((r) => (
            <button
              key={r.id}
              onClick={() => !isEditing && setRole(r.id)}
              disabled={isEditing}
              data-testid={`role-${r.id}`}
              className={`w-full text-left p-4 rounded-lg border transition-all ${
                role === r.id
                  ? 'border-amber-300 bg-amber-300/10 ring-1 ring-amber-300/40'
                  : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05]'
              } ${isEditing && role !== r.id ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-white">{r.label}</div>
                  <div className="text-xs text-white/50 mt-0.5">{r.hint}</div>
                </div>
                {role === r.id && <ShieldCheck className="w-5 h-5 text-amber-300" />}
              </div>
            </button>
          ))}
        </div>
        {isEditing && (
          <div className="text-[10px] text-white/40 -mt-6 mb-6 italic">Role can only be changed by an admin.</div>
        )}

        {role && (
          <div className="space-y-5 mb-8">
            {/* Basics */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">Headline</label>
              <input type="text" value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="onb-headline"
                placeholder="e.g. Enterprise AI & OT-IT Integration · 22 years in Manufacturing"
                className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">About / Bio</label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} data-testid="onb-bio"
                placeholder="Practitioner-flavoured summary — outcomes delivered, sectors, why you want to teach…"
                className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
            </div>
            {showInstitutionField && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">Institution</label>
                <input type="text" value={institution} onChange={(e) => setInstitution(e.target.value)} data-testid="onb-institution"
                  placeholder="e.g. IIT Bombay"
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
              </div>
            )}
            {showOrgField && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">Organisation</label>
                <input type="text" value={industryOrg} onChange={(e) => setIndustryOrg(e.target.value)} data-testid="onb-org"
                  placeholder="e.g. Tata Consultancy Services"
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">LinkedIn</label>
                <input type="url" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} data-testid="onb-linkedin"
                  placeholder="https://linkedin.com/in/…"
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">Location</label>
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="onb-location"
                  placeholder="e.g. Mumbai, India"
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
              </div>
            </div>

            {showExpertFields && (
              <>
                {/* Section: Expert profile */}
                <div className="pt-4 mt-6 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <Briefcase className="w-4 h-4 text-amber-300" />
                    <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">EXPERT PROFILE</div>
                  </div>
                </div>

                {role === 'industry_expert' && (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">Willing to serve as</label>
                    <div className="flex flex-wrap gap-2">
                      {ENGAGEMENT_ROUTES.map((r) => (
                        <button key={r.id} type="button" onClick={() => toggleRoute(r.id)} data-testid={`route-${r.id}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                            routes.includes(r.id)
                              ? 'bg-amber-300/20 text-amber-200 border-amber-300/40'
                              : 'bg-white/[0.02] text-white/70 border-white/10 hover:bg-white/[0.05]'
                          }`}>
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-white/70 mb-1.5">Years of experience</label>
                  <input type="number" min="0" value={years} onChange={(e) => setYears(e.target.value)} data-testid="onb-years"
                    placeholder="e.g. 22"
                    className="w-full sm:w-40 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
                </div>

                <TagInput label="Sectors"    values={sectors} setValues={setSectors} testIdPrefix="tag-sectors"    placeholder="e.g. Oil & Gas, Manufacturing" />
                <TagInput label="Domains"    values={domains} setValues={setDomains} testIdPrefix="tag-domains"    placeholder="e.g. Enterprise AI, Supply Chain" />
                <TagInput label="Key projects & outcomes"     values={projects} setValues={setProjects} testIdPrefix="tag-projects" placeholder='e.g. "Led AI deployment saving $12M @ Reliance"' />
                <TagInput label="Patents & publications"      values={patents}  setValues={setPatents}  testIdPrefix="tag-patents"  placeholder="e.g. US Patent 10,123,456 · AI-driven pump control" />
                <TagInput label="Certifications & awards"     values={certs}    setValues={setCerts}    testIdPrefix="tag-certs"    placeholder="e.g. AWS Solutions Architect, PMP" />

                {/* Resume */}
                {showResumeBox && (
                  <div className="pt-4 mt-6 border-t border-white/10">
                    <div className="flex items-center gap-2 mb-4">
                      <Award className="w-4 h-4 text-amber-300" />
                      <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">RESUME — VISIBLE TO UNIVERSITIES & FACULTY</div>
                    </div>
                    <ResumeBox user={user} refresh={refresh} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {err && <div className="text-xs text-rose-300 mb-3">{err}</div>}
        {msg && <div className="text-xs text-emerald-300 mb-3">{msg}</div>}

        <div className="flex items-center gap-3">
          <button onClick={submit} disabled={busy || !role} data-testid="onb-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-40 text-slate-900 text-sm font-semibold shadow-md">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isEditing ? 'Save profile' : 'Continue to my dashboard'}
          </button>
          {!isEditing && (
            <span className="text-[10px] text-white/40">Verification is manual for university, faculty & industry roles.</span>
          )}
        </div>
      </div>
    </PoPShell>
  );
};

export default PoPOnboarding;
